import { isAxiosError } from 'axios';
import { awsRum } from 'Utils/AwsRum';
import { summarizeError } from 'Utils/errorSummary';

/**
 * Report a caught error to AWS RUM (and the console).
 *
 * RUM does not scrape `console.*`, and errors caught in a try/catch,
 * `.catch()`, or a React error boundary never reach `window.onerror` — so
 * RUM's automatic telemetry never sees them. Every swallowed failure path
 * should call this so it becomes visible in RUM.
 *
 * `context` is a short, searchable tag for the RUM dashboard, e.g.
 * `'audio.unlock'`, `'iap.verify'`, `'http.interceptor'`. It is prefixed onto
 * the reported message without mutating the caught error (the original stack is
 * preserved so source-map de-minification still resolves the real frames).
 *
 * No-ops cleanly when RUM is disabled (`awsRum` is `undefined` in local/stg).
 *
 * @param error caught value of any type; normalized to an `Error`
 * @param context optional short tag identifying where the failure occurred
 */
export function reportError(error: unknown, context?: string): void {
  const normalized = error instanceof Error ? error : new Error(String(error));

  // RUM does not capture console; this is the local breadcrumb alongside the RUM record.
  // A failed request's error carries the body that was sent (for the account
  // calls a name, an email address or a phone number) and its headers, so only
  // its name, message and status are written. Any other error is written whole.
  console.error(
    context ? `[${context}] error:` : 'error:',
    isAxiosError(error) ? summarizeError(error) : error,
  );

  const reported = new Error(context ? `[${context}] ${normalized.message}` : normalized.message);
  reported.name = normalized.name;
  reported.stack = normalized.stack;
  awsRum?.recordError(reported);
}
